import axios from "axios"
import ApplicationState from "../../../Stores/applicationStates"
import SinglePublish, { updateSinglePublish, removeDataSinglePublish } from "../SinglePublish/Stores/singlePublishStore"
import { push, querystring } from "svelte-spa-router"
import { queryStringToJSON } from "../../../Script/queryString"

/**
 * Copies the given text to the clipboard using the Clipboard API or an alternative method.
 * @async
 * @param {string} textToCopy - The text to be copied to the clipboard.
 * @returns {Promise<void>} A Promise that resolves after the text is copied to the clipboard.
 */
export const copyToClipboard = async (textToCopy) => {
  // Navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy)
  } else {
    // Use the 'out of viewport hidden text area' trick
    const textArea = document.createElement("textarea")
    textArea.value = textToCopy

    // Move textarea out of the viewport so it's not visible
    textArea.style.position = "absolute"
    textArea.style.left = "-999999px"

    // Append textarea to the DOM
    document.body.prepend(textArea)

    // Select and copy the text
    textArea.select()
    try {
      document.execCommand("copy")
    } catch (error) {
      // console.error(error)
    } finally {
      // Clean up: Remove the textarea from the DOM
      textArea.remove()
    }
  }
}

/**
 * Checks if the provided expiration date is valid (not in the past).
 * @param {Event} e - The event object, typically from an input change event.
 * @returns {boolean} Returns `true` if the expiration date is valid, `false` otherwise.
 */
export const checkValidDate = (e) => {
  return new Promise((resolve, reject) => {
    try {
      // Convert the input value to a Date object
      let expirationDate = new Date(e.target.value)

      // Get the current date
      let currentDate = new Date()

      // Remove the time component from both dates
      expirationDate.setHours(0, 0, 0, 0)
      currentDate.setHours(0, 0, 0, 0)

      // Compare the dates
      if (expirationDate <= currentDate) {
        throw "You've selected an older date. Please choose a future date as the expiration date."
        // reject(`Don't choose older date.`) // Expiration date is in the past
      }
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}
export const checkValidOlderDate = (e) => {
  // Convert the input value to a Date object
  let expirationDate = new Date(e.target.value)

  // Get the current date
  let currentDate = new Date(Date.now())

  // Remove the time component from both dates
  expirationDate.setHours(0, 0, 0, 0)
  currentDate.setHours(0, 0, 0, 0)

  // Compare the dates
  if (expirationDate > currentDate) {
    return false // Expiration date is in the past
  } else {
    return expirationDate // Expiration date is valid
  }
}

/**
 * @type {initErrorNotify}
 */
let initErrorNotify = {
  close() {},
}

let query
querystring.subscribe((state) => (query = queryStringToJSON(state)))
let token
let viewBase
ApplicationState.subscribe((state) => {
  viewBase = state.viewBase
  token = state.token
})

export const getDocumentIdDetails = () => {
  if (!query.documentId) query.documentId = "000000000000000000000000"
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      let token
      ApplicationState.subscribe((state) => (token = state.token))
      // Fetching document id details
      const { data } = await axios.get(`/api/swagger/v2/documents/${query.documentId}`, {
        headers: {
          "x-access-token": token,
        },
      })
      // Check if the response has an error and is not successful
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
      }

      SinglePublish.update((singlePub) => {
        // singlePub = {}
        singlePub.documentId = data.document.id
        singlePub.Json = JSON.parse(data.document.metadata)
        singlePub.options = JSON.parse(data.document.options)
        singlePub.dataHash = data.document.dataHash
        singlePub.fileHash = data.document.fileHash
        singlePub.datePublished = data.document.datePublished
        if (data.document.fileHash) singlePub.uri = `/ipfs/${data.document.filename}`
        singlePub.options = JSON.parse(data.document.options)
        if (data.document.url) {
          singlePub.proposedURL = viewBase.replace("/v2/", `/v${data.document.version}/`) + data.document.url
          singlePub.url = data.document.url
        }
        return singlePub
      })
      SinglePublish.subscribe((data) => updateSinglePublish(data))
      // push(`/publish/single?documentId=${query.documentId}`)
      resolve()
    } catch (error) {
      if (error == "P2BCODE::DOCUMENT_DOES_NOT_EXIST") removeDataSinglePublish(query.documentId)
      SinglePublish.update((singlePub) => {
        return (singlePub = {})
      })

      reject(error)
    }
  })
}

export const releaseDocumentId = (documentId, force = false) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR"
      const { data } = await axios.delete(`/api/swagger/v2/documents/${documentId}/release?force=${force}`, {
        headers: {
          "x-access-token": token,
        },
      })

      // Check for errors in the API response
      if (data.error && !data.success) {
        throw data.errorCode // Throw the error code
        // reject(data.errorCode)
      }
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const validateFileType = (file) => {
  return new Promise((resolve, reject) => {
    // Create a new instance of FileReader
    const filereader = new FileReader()

    // Define the callback function for when the file is loaded
    filereader.onloadend = (e) => {
      // Extract the first 4 bytes of the file as an Uint8Array
      const arr = new Uint8Array(e.target.result).subarray(0, 4)
      let header = ""

      // Iterate over the bytes and convert them to hexadecimal representation
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16)
      }

      // Check if the header matches the expected values for JPEG or PNG and pdf
      if (header === "89504e47" || header === "ffd8ffee" || header === "ffd8ffe0" || header === "ffd8ffe1" || header == "25504446") {
        // if (header === "89504e47" || header === "ffd8ffe0") {
        // If the header matches either format, resolve the Promise with the file object
        resolve(file)
      } else {
        // If the header doesn't match, reject the Promise with an error message
        reject("Invalid Format")
      }
    }

    // Read the first 4 bytes of the file as an ArrayBuffer
    filereader.readAsArrayBuffer(file.slice(0, 4))
  })
}
